import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LegalFeeStage } from '@/types/legalFees';
import { ItemRow } from './ItemRow';
import { StagePaymentForm } from './StagePaymentForm';

interface StageCardProps {
  stage: LegalFeeStage;
  fixedAssetId: string;
  propertyName: string;
  assetCurrency: string;
}

export function StageCard({ stage, fixedAssetId, propertyName, assetCurrency }: StageCardProps) {
  const [stagePayOpen, setStagePayOpen] = useState(false);

  const totalItems = stage.items.length;
  const paidItems = stage.items.filter((i) => i.paid_status === 'paid').length;
  const hasUnpaid = stage.items.some((i) => i.paid_status === 'unpaid');
  const allPaid = totalItems > 0 && paidItems === totalItems;
  const noItems = totalItems === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <CardTitle className="text-base">{stage.name}</CardTitle>
          {noItems ? (
            <Badge variant="secondary">No Items</Badge>
          ) : allPaid ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
              Stage Complete
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">
              {paidItems} of {totalItems} paid
            </span>
          )}
        </div>

        {hasUnpaid && (
          <Button size="sm" onClick={() => setStagePayOpen(true)}>
            Pay Stage
          </Button>
        )}
      </CardHeader>

      {totalItems > 0 && (
        <CardContent className="pt-0 divide-y">
          {stage.items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              stageName={stage.name}
              fixedAssetId={fixedAssetId}
              propertyName={propertyName}
            />
          ))}
        </CardContent>
      )}

      {/* Stage payment dialog */}
      <Dialog open={stagePayOpen} onOpenChange={setStagePayOpen}>
        <DialogContent data-testid="stage-payment-dialog">
          <DialogHeader>
            <DialogTitle>Pay Stage: {stage.name}</DialogTitle>
          </DialogHeader>
          <StagePaymentForm
            stage={stage}
            fixedAssetId={fixedAssetId}
            propertyName={propertyName}
            assetCurrency={assetCurrency}
            onSuccess={() => setStagePayOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
